/**
 * LandingPageObs — the "Observatory" landing page.
 *
 * Sits alongside LandingObservatory (the CRO-restructured alternative) without
 * colliding: different export name, and different CSS prefixes — this file uses
 * `rb-`, the other uses `ob-` — so mounting either cannot affect the other.
 *
 * How the two differ, if you are still choosing:
 *
 *   THIS FILE                         LandingObservatory
 *   ─────────────────────────────     ────────────────────────────────────────
 *   Nav has a "Plans" link            No section links (every nav link is an exit)
 *   Two CTAs in the hero              One (multi-CTA heroes measure worse now)
 *   No sticky CTA                     Sticky bar, the largest single lift (+11%)
 *   No proof in the hero              Proof slots beside the CTA (empty until real)
 *   Orrery first on mobile            Copy first; CTA must be above the fold
 *   Fonts via @import (blocking)      Preconnect + non-blocking attach
 *   No FAQ                            FAQ before the final CTA
 *   No guarantee line                 Guarantee under every CTA
 *
 * The plan copy in BOTH now comes from config.js → TIERS, so choosing between
 * them is a layout decision only; the claims cannot diverge.
 */

/**
 * =============================================================================
 * ROASTBREW — LANDING: "THE OBSERVATORY"
 * =============================================================================
 *
 * Direction A of two. Dark, celestial, spectacle-forward. Aimed at the STUDENT
 * and at a parent who wants to feel the product is unlike the free alternatives.
 *
 * ── WHY THIS LOOKS THE WAY IT DOES ───────────────────────────────────────────
 * The brief pins the visual direction: this site already has an identity — the
 * pro/pro_plus palette (#1A1A1A / #0D0D0D with the #C9A962 gold), Bodoni Moda
 * for display, DM Sans for body and labels, hairline .5px
 * borders, radial-gradient cores, backdrop blur — and a signature view in the
 * Courses page's orbital system. A landing page that invented a new identity
 * would be a worse landing page, so this one extends the existing one exactly.
 *
 * ── THE SIGNATURE ────────────────────────────────────────────────────────────
 * The hero is not a headline over a screenshot. It is a live orrery, drawn in
 * SVG from the same primitives the Courses page uses: a subject body with a
 * progress arc, course medallions on a slow orbital ring. The most
 * characteristic thing in the product's world, used as the thesis.
 *
 * ── PROPS ────────────────────────────────────────────────────────────────────
 * onGetStarted(tierKey)  start checkout / signup for a tier
 * onSignIn()             existing customer
 * prices                 OPTIONAL { free:{amount,currency,interval}, pro:{...}, pro_plus:{...} }
 *                        Same shape as /billing/prices (amounts in cents). When not
 *                        given, the page reads /billing/public-prices itself (no
 *                        sign-in). Stripe is the only source of the numbers: no
 *                        amount is hard-coded; a missing price shows "See more".
 * =============================================================================
 */

import React, { useEffect, useRef, useState } from 'react';
import { TIERS } from './config';
import { T, PublicTheme } from './publicTheme';
import BrandLockup from './BrandLockup';


// Prices come only from Stripe, through the server's public, read-only /billing/public-prices (no sign-in, amounts
// only). No amount is hard-coded: while loading, or if a plan has no price, its card shows "See more", which
// goes where the card's own button goes (sign-up, then the plans page with Stripe's price). Free is $0.
const API_URL = process.env.REACT_APP_API_URL;
const FREE_PRICE = { amount: 0, currency: 'usd', interval: null };

// Written for THIS product. config.js TIERS still carries chatbot boilerplate
// ("5 conversations per month", "API access with 10,000 calls/month") which is
// currently rendering inside ManageSubscription — that needs the same rewrite,
// and then both surfaces should read from one place.
// ── PLANS COME FROM config.js ────────────────────────────────────────────────
// This used to be a hand-written array, which meant the landing page and the
// pricing page each carried their own copy of the same claims — and they drifted:
// this file still advertised "Pre-algebra through Calculus, unlocked" after
// Pre-calculus and Calculus had been pulled from the offer. TIERS is now the one
// source, so a copy change lands in both places at once.
//
// Mapping: TIERS gives { name, description, features, badge }. `description` is
// the one-line pitch under the plan name — the field this file called `line`.
//
// HIDDEN_TIERS mirrors the constant in ManageSubscription.jsx. It is duplicated
// rather than exported from config because visibility is a VIEW decision and
// config defines what a tier IS, not whether it is currently buyable.
// ⚠️ These two arrays must be kept in step. If a third surface ever needs it,
// promote it to a shared view-layer module rather than adding a third copy.
const HIDDEN_TIERS = ['pro_plus'];

const PLANS = Object.entries(TIERS)
  .filter(([key]) => !HIDDEN_TIERS.includes(key))
  .sort((a, b) => a[1].rank - b[1].rank)
  .map(([key, t]) => ({
    key,
    name: t.name,
    line: t.description,
    features: t.features,
    badge: t.badge,
  }));

// The real four-part shape of a RoastBrew lesson (CONTRACT.md §2). This is a
// SET, not a sequence, so it is not numbered 01/02/03 — that would imply an
// order the content does not have.
const PACKAGE = [
  { t: 'The lesson',        d: 'A full taught video. Not a summary, not a highlight reel.' },
  { t: 'The homework',      d: 'A problem set built for that lesson, ready to print.' },
  { t: 'The worked answers', d: 'A second video solving the set, question by question.' },
  { t: 'The answer key',    d: 'The written solutions, for checking at a glance.' },
];

// The ladder is the visual centrepiece, so it keeps all six rungs — cutting it to
// four would lose a third of its height and the sense of a climb. The last two
// carry `soon` instead, which is honest AND does useful work: it shows the
// direction of travel rather than hiding it, and it agrees with the plan card
// below ("Pre-calculus and Calculus included free as they ship").
//
// WHEN THEY SHIP: delete the two `soon: true` flags. Nothing else changes.
// Spelled out, so the heading above the plan grid can never contradict the number
// of cards beneath it.
const NUMBER_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five'];
const PLAN_COUNT_WORD = NUMBER_WORDS[PLANS.length] || String(PLANS.length);

const LADDER = [
  { name: 'Pre-algebra' },
  { name: 'Algebra I' },
  { name: 'Geometry' },
  { name: 'Algebra II' },
  { name: 'Pre-calculus', soon: true },
  { name: 'Calculus', soon: true },
];

// ─────────────────────────────────────────────────────────────────────────────
// THE ORRERY — the signature. Same primitives as CoursesPage: a body with a
// progress arc, medallions on a ring. Rotation is CSS so it costs nothing and
// is switched off under prefers-reduced-motion.
// ─────────────────────────────────────────────────────────────────────────────
function Orrery() {
  const R = 132;                       // orbit radius
  const arc = { r: 56, pct: 68 };
  const C = 2 * Math.PI * arc.r;
  return (
    <div className="rb-orrery" aria-hidden="true">
      <div className="rb-orrery__halo" />
      <svg viewBox="-190 -190 380 380" className="rb-orrery__svg">
        {/* orbit path */}
        <circle cx="0" cy="0" r={R} fill="none" stroke={T.hairline} strokeWidth="1" />
        {/* the body */}
        <circle cx="0" cy="0" r={arc.r} fill="url(#rb-core)" stroke="rgba(232,213,163,0.22)" strokeWidth="0.5" />
        <circle cx="0" cy="0" r={arc.r} fill="none" stroke="rgba(232,213,163,0.10)" strokeWidth="2.5" />
        <circle cx="0" cy="0" r={arc.r} fill="none" stroke={T.gild} strokeWidth="2.5"
          strokeDasharray={C} strokeDashoffset={C * (1 - arc.pct / 100)}
          strokeLinecap="round" transform="rotate(-90)" className="rb-orrery__arc" />
        <text x="0" y="10" textAnchor="middle" fill={T.gild}
          style={{ fontFamily: T.display, fontSize: 30, fontWeight: 300 }}>∑</text>

        {/* course medallions, one per rung of the ladder */}
        <g className="rb-orrery__ring">
          {LADDER.map((c, i) => {
            const a = (i / LADDER.length) * Math.PI * 2 - Math.PI / 2;
            const x = Math.cos(a) * R, y = Math.sin(a) * R;
            const done = i < 2;
            // Unbuilt courses read as fainter bodies — the same information the
            // ladder's "In production" tag carries, in the hero's own language.
            return (
              <g key={c.name} transform={`translate(${x} ${y})`} opacity={c.soon ? 0.4 : 1}>
                <circle r="13" fill="url(#rb-med)" stroke={done ? T.gold : 'rgba(232,213,163,0.20)'} strokeWidth="0.5" />
                {!c.soon && (
                  <circle r="13" fill="none" stroke={T.gold} strokeWidth="1.5"
                    strokeDasharray={2 * Math.PI * 13}
                    strokeDashoffset={2 * Math.PI * 13 * (done ? 0 : 0.72)}
                    strokeLinecap="round" transform="rotate(-90)" opacity="0.85" />
                )}
              </g>
            );
          })}
        </g>

        <defs>
          <radialGradient id="rb-core" cx="35%" cy="32%">
            <stop offset="0%" stopColor="rgba(232,213,163,0.28)" />
            <stop offset="55%" stopColor="rgba(201,169,98,0.06)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <radialGradient id="rb-med" cx="35%" cy="32%">
            <stop offset="0%" stopColor="rgba(232,213,163,0.22)" />
            <stop offset="100%" stopColor="rgba(13,13,13,0.9)" />
          </radialGradient>
        </defs>
      </svg>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   STATEMENT PANEL
   ─────────────────────────────────────────────────────────────────────────────
   A full-bleed line between sections. Long-form sales pages have used recurring
   statement panels for decades — Apple and Basecamp both build on it — because a
   promise restated a different way lands with readers the first framing missed.

   THE RULE THAT KEEPS IT FROM BECOMING SIX ADVERTS: the panels are ONE argument
   in acts, not six unrelated pitches. Same reason three placements of one CTA
   beat six different CTAs. Sequence here is
       hero    (access)   -> "we taught the 1%"
       panel 1 (rescue)   -> "stuck is normal"
       panel 2 (release)  -> "you don't have to remember the math"
   System, then moment, then permission. Each panel sits AFTER the section that
   earns it.

   It must also be a DIFFERENT KIND OF THING to a section, or the page reads as
   several heroes in a coat: darker ground, no eyebrow, no numeral, no button, no
   body copy beyond one supporting sentence, and set smaller than the hero so the
   hero stays the hero.
   ───────────────────────────────────────────────────────────────────────────*/
function Statement({ lines, children, reveal }) {
  return (
    <section className="rb-stmt" ref={reveal}>
      <h2 className="rb-stmt__h">
        {lines.map((l, i) => (
          <span key={i}>{l}{i < lines.length - 1 && <br />}</span>
        ))}
      </h2>
      {children && <p className="rb-stmt__p">{children}</p>}
    </section>
  );
}

// A real, paid amount from Stripe (cents). Anything else shows "See more" and no "/month".
const hasPaidAmount = (p) => !!p && typeof p.amount === 'number' && Number.isFinite(p.amount) && p.amount > 0;

// Amounts are in cents, as Stripe gives them. "$9.99"; a whole amount shows without cents ("$20").
function money(p) {
  if (!p || typeof p.amount !== 'number' || !Number.isFinite(p.amount) || p.amount < 0) return null;
  if (p.amount === 0) return 'Free';
  try {
    const whole = p.amount % 100 === 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (typeof p.currency === 'string' ? p.currency : 'usd').toUpperCase(),
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(p.amount / 100);
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
export default function LandingPageObs({ onGetStarted = () => {}, onSignIn = () => {}, prices }) {
  const [stripePrices, setStripePrices] = useState(null);
  const P = { free: FREE_PRICE, ...(prices || stripePrices || {}) };
  const [lit, setLit] = useState(false);

  // Read Stripe's prices once (no sign-in). A failure leaves "See more"; nothing made-up is ever shown.
  useEffect(() => {
    if (prices) return undefined;
    let gone = false;
    fetch(`${API_URL}/billing/public-prices`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!gone && data && data.prices && typeof data.prices === 'object') setStripePrices(data.prices);
      })
      .catch(() => {});
    return () => { gone = true; };
  }, [prices]);
  const revealRef = useRef([]);

  useEffect(() => { const t = setTimeout(() => setLit(true), 60); return () => clearTimeout(t); }, []);

  // Scroll reveal, one observer for every marked section.
  useEffect(() => {
    const els = revealRef.current.filter(Boolean);
    if (!('IntersectionObserver' in window)) { els.forEach((e) => e.classList.add('is-in')); return undefined; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, []);

  const mark = (i) => (el) => { revealRef.current[i] = el; };

  return (
    <div className="rb-land rb-public">
      <PublicTheme />
      {/* ── NAV ────────────────────────────────────────────────────────── */}
      <header className="rb-nav">
        {/* The wordmark scrolls to top rather than linking away.
            On an AD LANDING PAGE every outbound link is an exit, and "home" here
            is the sign-in form — a dead end for someone who has not decided yet.
            Scroll-to-top gives the click the behaviour people expect from a logo
            without spending the visitor. */}
        <button type="button" className="rb-nav__mark"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top">
          <BrandLockup tone="dark" height={34} />
        </button>
        <nav className="rb-nav__right">
          <a href="#plans" className="rb-nav__link">Plans</a>
          <button type="button" className="rb-nav__signin" onClick={onSignIn}>Sign in</button>
        </nav>
      </header>

      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section className={'rb-hero' + (lit ? ' is-lit' : '')}>
        <Orrery />
        <div className="rb-hero__copy">
          {/* THREE WORDS. Set in the display serif at full caps with wide tracking
              — the treatment a fashion house uses on a shopfront, not the one a
              SaaS uses on a pricing page. Everything explanatory moved below it.
              text-wrap:balance keeps the two-line break even at any width. */}
          {/* HEADLINE — the access reversal.
              It was "MATH TAUGHT RIGHT", which is a claim every tutoring company
              on earth makes and a visitor cannot check. This is the one thing on
              the page nobody else can say, and it was previously buried in grey
              text below the fold-line of attention. Ogilvy's rule: eight of ten
              read the headline, two of ten read the rest. Put the asset in the
              headline. */}
          <h1 className="rb-hero__h">
            We Taught The 1%<br />For Ten Years
          </h1>

          <p className="rb-hero__sub">
            <em>Now we teach everyone.</em> The same courses, the same instructors,
            the same homework we built behind closed doors &mdash; rebuilt so any
            family can open it tonight.
          </p>

          <div className="rb-hero__cta">
            <button type="button" className="rb-btn rb-btn--gold" onClick={() => onGetStarted('pro')}>
              Sign Up
            </button>
            <button type="button" className="rb-btn rb-btn--ghost" onClick={onSignIn}>
              Log In
            </button>
          </div>
          <p className="rb-hero__note">Free tier &middot; No card required</p>

          {/* Scroll affordance. Without it the fold reads as the whole page. */}
          <a href="#package" className="rb-scroll">
            <span className="rb-scroll__label">Learn More</span>
            <svg className="rb-scroll__arrow" width="16" height="24" viewBox="0 0 16 24"
              fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <path d="M8 3v16" /><path d="M3 14l5 5 5-5" />
            </svg>
          </a>
        </div>
      </section>

      {/* ── THE PACKAGE ────────────────────────────────────────────────── */}
      <section className="rb-sec" id="package" ref={mark(0)}>
        <p className="rb-sec__eyebrow">Every lesson, four parts</p>
        <h2 className="rb-sec__h">Most platforms hand you a video and wish you luck.</h2>
        <div className="rb-pack">
          {PACKAGE.map((p) => (
            <article key={p.t} className="rb-pack__item">
              <span className="rb-pack__dot" />
              <h3 className="rb-pack__t">{p.t}</h3>
              <p className="rb-pack__d">{p.d}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ACT 2 — RESCUE. Placed after the four-parts grid on purpose: "someone is
          always there" only means something once the reader knows what a lesson
          actually contains. */}
      <Statement reveal={mark(4)} lines={['Stuck Is Normal.', 'Staying Stuck Isn\u2019t.']}>
        Everyone gets stuck on question seven. The difference is whether someone is
        there to work it through with you. Now someone always is.
      </Statement>


      {/* ── THE LADDER ─────────────────────────────────────────────────── */}
      <section className="rb-sec rb-sec--tight" ref={mark(1)}>
        <p className="rb-sec__eyebrow">The whole ladder</p>
        <h2 className="rb-sec__h">One continuous course, not a library of clips.</h2>
        <p className="rb-sec__p">
          Not a library of loose videos. One continuous course, in order, so a
          student can climb it without guessing what comes next.
        </p>
        <ol className="rb-ladder">
          {LADDER.map((c, i) => (
            <li key={c.name} className={'rb-ladder__rung' + (c.soon ? ' rb-ladder__rung--soon' : '')} style={{ '--i': i }}>
              <span className="rb-ladder__name">{c.name}</span>
              {c.soon && <span className="rb-ladder__soon">In production</span>}
            </li>
          ))}
        </ol>
      </section>

      {/* ACT 3 — RELEASE. Speaks to the PARENT, not the student, and arrives just
          before the price. Research on parental math anxiety found guilt about
          being unable to help is one of the strongest drivers in the category —
          and essentially nobody markets to it. */}
      <Statement reveal={mark(5)} lines={['You Don\u2019t Have To', 'Remember The Math']}>
        {/* Three deliberate lines, not a wrapped paragraph. The rhythm is the
            argument: the long line names every burden, the short one takes them
            away, the last one gives something back. Wrapped as one block that
            cadence disappears. */}
        <span className="rb-stmt__line">
          You don&rsquo;t have to teach it, check it, or pretend you know it.
        </span>
        <span className="rb-stmt__line">We do that part.</span>
        <span className="rb-stmt__line">You get to be their parent again.</span>
      </Statement>


      {/* ── PLANS ──────────────────────────────────────────────────────── */}
      <section className="rb-sec" id="plans" ref={mark(2)}>
        <p className="rb-sec__eyebrow">Plans</p>
        {/* Counted from PLANS, not hardcoded. It said "Three ways in" while
            Pro+ was hidden and only two cards rendered. */}
        <h2 className="rb-sec__h">{PLAN_COUNT_WORD} ways in.</h2>
        <div className="rb-plans">
          {PLANS.map((pl) => {
            const price = P[pl.key];
            const feat = pl.key === 'pro';
            return (
              <article key={pl.key} className={'rb-plan' + (feat ? ' rb-plan--feat' : '')}>
                {pl.badge && <span className="rb-plan__badge">{pl.badge}</span>}
                <h3 className="rb-plan__name">{pl.name}</h3>
                <p className="rb-plan__line">{pl.line}</p>
                <p className="rb-plan__price">
                  {pl.key === 'free' || (hasPaidAmount(price) && money(price) !== null) ? (
                    <>
                      <span className="rb-plan__amt">{money(price) || 'Free'}</span>
                      {hasPaidAmount(price) && price.interval ? <span className="rb-plan__per">/{price.interval}</span> : null}
                    </>
                  ) : (
                    // No price from Stripe (still loading, or it couldn't be read): never a made-up number. The link
                    // goes the same way as this card's button, to the page that shows Stripe's price (Vinay, Nov 2).
                    <button type="button" className="rb-plan__seeprice" onClick={() => onGetStarted(pl.key)}>
                      See more
                    </button>
                  )}
                </p>
                <ul className="rb-plan__feats">
                  {pl.features.map((f) => (
                    <li key={f}><span className="rb-plan__tick" aria-hidden="true">
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke={T.gold}
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 6.4 L4.6 9 L10 3.2" />
                      </svg>
                    </span>{f}</li>
                  ))}
                </ul>
                <button type="button"
                  className={'rb-btn ' + (feat ? 'rb-btn--gold' : 'rb-btn--ghost') + ' rb-plan__cta'}
                  onClick={() => onGetStarted(pl.key)}>
                  {pl.key === 'free' ? 'Start free' : 'Choose ' + pl.name}
                </button>
              </article>
            );
          })}
        </div>
      </section>

      {/* ── CLOSE ──────────────────────────────────────────────────────── */}
      <section className="rb-close" ref={mark(3)}>
        {/* No terminal full stops. A two-line sign-off in display capitals reads as
            a mark, not a sentence — the period makes it a statement being finished
            rather than a name being given. */}
        <h2 className="rb-close__h">This is RoastBrew<br /><em>Learn Something New</em></h2>
        <button type="button" className="rb-btn rb-btn--gold" onClick={() => onGetStarted('pro')}>
          Start learning
        </button>
      </section>

      <footer className="rb-foot">
        <span>© {new Date().getFullYear()} RoastBrew</span>
        <span className="rb-foot__links">
          <a href="#plans">Plans</a><a href="/privacy">Privacy</a><a href="/terms">Terms</a>
        </span>
      </footer>

      <style>{`
        /* ═══════════════════════════════════════════════════════════════════
           WHAT USED TO BE AT THE TOP OF THIS BLOCK, AND WHERE IT WENT
           ───────────────────────────────────────────────────────────────────
           The font @import, the html/body paint, the scrollbar styling, the
           measures, the type and spacing scales, the focus ring and the .rb-btn
           kit are all in publicTheme.js now. They were identical on every
           pre-auth screen and each screen was redeclaring them; @import is also
           only valid at the top of a stylesheet, so it could not be shared any
           other way.

           Nothing about this page's appearance changed. The shared rules are
           scoped to ".rb-public, .rb-land", and the root element carries both
           classes, so every --m-*, --t-* and --s-* variable used below still
           resolves exactly as before.

           publicTheme injects into <head>; this block renders in the <body>.
           Body wins at equal specificity, which is what makes the page-local
           overrides further down (e.g. .rb-btn in the landscape media query)
           still take effect.

           Only the section counter is genuinely local to this page.
           ═══════════════════════════════════════════════════════════════════ */
        .rb-land{counter-reset:rb-sec}

        /* NAV */
        .rb-nav{position:sticky;top:0;z-index:20;display:flex;align-items:center;justify-content:space-between;
          padding:18px 24px;background:rgba(13,13,13,0.72);backdrop-filter:blur(14px);
          -webkit-backdrop-filter:blur(14px);border-bottom:.5px solid ${T.hairline}}
        .rb-nav__mark{display:flex;align-items:center;gap:11px;background:none;border:none;
          padding:0;cursor:pointer;font:inherit;color:inherit;min-height:44px}
        .rb-nav__right{display:flex;align-items:center;gap:6px}
        .rb-nav__link{color:${T.muted};text-decoration:none;font-family:${T.label};
          font-size:var(--t-label);font-weight:500;letter-spacing:.18em;text-indent:.18em;
          text-transform:uppercase;padding:12px 14px}
        .rb-nav__signin{background:none;border:.5px solid ${T.hairline};color:${T.parchment};
          font-family:${T.label};font-size:var(--t-label);font-weight:500;letter-spacing:.18em;
          text-indent:.18em;text-transform:uppercase;padding:12px 20px;border-radius:2px;cursor:pointer;
          min-height:44px;transition:border-color .25s,background .25s}
        @media (hover:hover){.rb-nav__link:hover{color:${T.parchment}}
          .rb-nav__signin:hover{border-color:${T.gold};background:rgba(201,169,98,0.08)}}

        /* HERO */
        /* HERO SPACING — measured against real viewports.
           Before: padding 64/96 with a 380px orrery put the CTA at y≈897 on a
           900px desktop and y≈703 on a 667px iPhone — below the fold on both, so
           the buttons were cut off exactly as reported.
           Now: the hero is a min-height:100svh flex column that CENTRES its
           content, so it adapts to the viewport instead of assuming one. Padding
           is trimmed and the orrery is capped by viewport HEIGHT as well as
           width (see .rb-orrery), which is what actually caused the overflow. */
        .rb-hero{position:relative;display:flex;flex-direction:column;align-items:center;
          justify-content:center;min-height:calc(100svh - 70px);
          text-align:center;padding:24px 24px 40px;background:
            radial-gradient(ellipse at 50% 22%, rgba(201,169,98,0.10) 0%, transparent 58%),
            linear-gradient(${T.void}, ${T.ink} 62%)}
        /* min() against svh as well as vw: at 380px this alone was 42% of a 900px
           desktop and 57% of a 667px phone, which is what pushed the CTA off. */
        .rb-orrery{position:relative;width:min(320px,74vw,24svh);aspect-ratio:1;margin-bottom:-14px;
          opacity:0;transform:scale(.94);transition:opacity 1.1s ease,transform 1.4s cubic-bezier(.2,.7,.3,1)}
        .rb-hero.is-lit .rb-orrery{opacity:1;transform:scale(1)}
        .rb-orrery__svg{width:100%;height:100%;overflow:visible}
        .rb-orrery__halo{position:absolute;inset:14%;border-radius:50%;
          background:radial-gradient(circle,rgba(201,169,98,0.20),transparent 68%);filter:blur(34px)}
        .rb-orrery__ring{transform-origin:0 0;animation:rb-spin 132s linear infinite}
        .rb-orrery__arc{animation:rb-draw 2.6s cubic-bezier(.2,.7,.3,1) both .5s}
        @keyframes rb-spin{to{transform:rotate(360deg)}}
        @keyframes rb-draw{from{stroke-dashoffset:${2 * Math.PI * 56}}}

        .rb-hero__copy{max-width:var(--m-wide);width:100%}
        /* Each element of the hero arrives on its own beat rather than the block
           sliding up as one. The headline leads; everything else follows it. */
        .rb-hero__copy > *{opacity:0;transform:translateY(14px);
          transition:opacity .9s ease,transform .9s cubic-bezier(.2,.7,.3,1)}
        .rb-hero.is-lit .rb-hero__copy > *{opacity:1;transform:none}
        .rb-hero.is-lit .rb-hero__h{transition-delay:.15s}
        .rb-hero.is-lit .rb-hero__sub{transition-delay:.42s}
        .rb-hero.is-lit .rb-hero__cta{transition-delay:.60s}
        .rb-hero.is-lit .rb-hero__note{transition-delay:.72s}
        .rb-hero.is-lit .rb-scroll{transition-delay:.90s}
        /* THE HEADLINE.
           Full caps in the display serif with POSITIVE tracking — the opposite of
           the tight negative tracking a tech page uses. Caps at .04em read as
           engraved; caps at -.02em read as cramped. Weight stays at 300: the size
           does the shouting, not the ink.
           text-wrap:balance keeps the two lines even at any width. */
        /* THE HEADLINE.
           Bodoni caps at display size take NEGATIVE tracking, not positive. This
           is the opposite of the usual "caps need air" rule and it is why the
           previous pass looked wrong: Didone capitals are already generously
           side-beared, so adding .04em on top pulls the word apart into letters.
           A Vogue cover sets its masthead nearly touching. -.015em here.
           opsz pinned to 96 so the hairlines stay razor-thin at size — that
           contrast IS the luxury signal. */
        .rb-hero__h{font-family:${T.display};font-weight:400;
          font-size:var(--t-display);line-height:.86;letter-spacing:-.015em;
          text-transform:uppercase;text-wrap:balance;
          /* min(100%, ...) so the cap can never resolve wider than the container.
             20ch on display type is ~1500px, which never constrained anything. */
          max-width:min(100%, var(--m-tight));margin:0 auto var(--s-4);
          color:${T.parchment}}

        /* The claim, demoted to a subhead. Italic gild on the boast only, so the
           eye lands there without the whole paragraph shouting. */
        /* The lead sits on --m-read and is centred like everything else. At 1.75
           the leading is looser than a paragraph would normally take, which is
           what stops a centred block reading as cramped. */
        .rb-hero__sub{font-size:var(--t-lead);line-height:1.75;color:${T.muted};
          max-width:var(--m-read);margin:0 auto var(--s-4);
          text-wrap:pretty}
        .rb-hero__sub em{font-family:${T.display};font-style:italic;font-weight:400;
          color:${T.gild};opacity:.95;font-size:1.06em;letter-spacing:.005em}
        .rb-hero__cta{display:flex;flex-wrap:wrap;gap:var(--s-2);justify-content:center}
        .rb-hero__note{font-family:${T.label};font-size:var(--t-label);font-weight:400;
          letter-spacing:.2em;text-indent:.2em;text-transform:uppercase;color:${T.muted};opacity:.62;
          margin:var(--s-3) 0 0}

        /* SCROLL CUE — the only thing telling a visitor the fold is not the page.
           The arrow drifts rather than bounces; a bounce reads as an app, a drift
           reads as an invitation. */
        .rb-scroll{display:inline-flex;flex-direction:column;align-items:center;gap:10px;
          margin-top:clamp(34px,5vh,64px);text-decoration:none;color:${T.muted};
          transition:color .3s ease}
        .rb-scroll__label{font-family:${T.label};font-size:var(--t-label);font-weight:500;
          letter-spacing:.28em;text-indent:.28em;text-transform:uppercase}
        .rb-scroll__arrow{animation:rb-drift 2.6s cubic-bezier(.4,0,.2,1) infinite}
        @keyframes rb-drift{
          0%,100%{transform:translateY(0);opacity:.45}
          50%{transform:translateY(7px);opacity:1}}
        @media (hover:hover){.rb-scroll:hover{color:${T.gild}}}

        /* BUTTONS */
        /* The .rb-btn kit — gold and ghost, with the optical-centring
           correction — lives in publicTheme.js. Every pre-auth screen uses it. */

        /* SECTIONS */
        /* SECTION TRANSITION.
           The whole section used to fade as one block, which reads as a slide
           appearing. Now the eyebrow, the headline and the body arrive in
           sequence — 0ms, 90ms, 180ms — so a section ASSEMBLES itself. Same
           total duration, entirely different feel.
           A hairline also draws across the top of each section as it enters. */
        /* EVERY SECTION CENTRES. The single biggest change on the page: the hero
           and the close were centred while the three sections between them were
           left-aligned, so the eye had to re-find the axis four times on the way
           down. One axis, top to bottom. */
        .rb-sec{counter-increment:rb-sec;position:relative;max-width:var(--m-wide);
          margin:0 auto;padding:var(--s-7) 24px;text-align:center}
        .rb-sec::before{content:'';position:absolute;top:0;left:24px;right:24px;height:1px;
          background:linear-gradient(90deg,${T.hairline},transparent);
          transform:scaleX(0);transform-origin:left;
          transition:transform 1.1s cubic-bezier(.2,.7,.3,1)}
        .rb-sec.is-in::before{transform:scaleX(1)}
        .rb-sec > *{opacity:0;transform:translateY(18px);
          transition:opacity .7s ease,transform .7s cubic-bezier(.2,.7,.3,1)}
        .rb-sec.is-in > *{opacity:1;transform:none}
        .rb-sec.is-in > *:nth-child(1){transition-delay:0ms}
        .rb-sec.is-in > *:nth-child(2){transition-delay:90ms}
        .rb-sec.is-in > *:nth-child(3){transition-delay:180ms}
        .rb-sec.is-in > *:nth-child(4){transition-delay:260ms}
        .rb-sec.is-in > *:nth-child(5){transition-delay:340ms}
        .rb-sec--tight{padding-top:40px}
        /* SECTION EYEBROW — a numbered rule, not a floating label. The counter and
           the hairline give every section the same opening gesture, which is what
           makes a page read as designed rather than assembled. */
        /* A numeral between two SYMMETRICAL hairlines. The previous version put a
           rule on one side only — a left-aligned gesture that made a centred label
           look stranded rather than placed. */
        .rb-sec__eyebrow{display:flex;align-items:center;justify-content:center;gap:var(--s-2);
          font-family:${T.label};font-size:var(--t-label);font-weight:500;letter-spacing:.3em;
          text-indent:.3em;text-transform:uppercase;color:${T.gold};margin:0 0 var(--s-3)}
        .rb-sec__eyebrow::before,.rb-sec__eyebrow::after{content:'';
          width:clamp(20px,4vw,48px);height:1px;background:${T.gold};opacity:.4;text-indent:0}

        /* SECTION HEADLINE — sentence case in the display serif, tracking near
           zero. Caps are reserved for the hero and the eyebrows, so they keep
           their force; using them everywhere would spend it. */
        /* Sentence case, deliberately. Caps are spent on the hero, the close, the
           eyebrows and the buttons; using them here too would leave nothing to
           distinguish. opsz 72 keeps contrast high without the hairline thinning
           you get pinning it to 96 at this size. */
        .rb-sec__h{font-family:${T.display};font-weight:400;
          font-size:var(--t-h2);line-height:1.08;letter-spacing:-.018em;
          margin:0 auto var(--s-3);max-width:min(100%, var(--m-tight));text-wrap:balance}
        .rb-sec__p{font-size:var(--t-lead);line-height:1.75;color:${T.muted};
          max-width:var(--m-read);margin:0 auto var(--s-5);text-wrap:pretty}

        /* PACKAGE */
        /* Cards centre with the rest of the page, and the hairline grid loses its
           outer border and radius — a boxed card reads as a UI component, four
           columns divided by hairlines reads as a printed spec sheet. */
        .rb-pack{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:1px;
          margin-top:var(--s-5);background:${T.hairline};
          border-top:1px solid ${T.hairline};border-bottom:1px solid ${T.hairline}}
        .rb-pack__item{background:${T.ink};padding:var(--s-4) var(--s-3);text-align:center}
        .rb-pack__dot{display:block;width:5px;height:5px;border-radius:50%;background:${T.gold};
          box-shadow:0 0 14px rgba(201,169,98,.9);margin:0 auto var(--s-3)}
        .rb-pack__t{font-family:${T.label};font-weight:500;font-size:var(--t-label);
          letter-spacing:.22em;text-indent:.22em;text-transform:uppercase;color:${T.parchment};
          margin:0 0 var(--s-2)}
        .rb-pack__d{font-size:var(--t-small);line-height:1.7;color:${T.muted};margin:0 auto;max-width:26ch}

        /* LADDER — each rung sets slightly larger, so the climb is visible in the type */
        /* THE LADDER — a pyramid, built from the base up.
           Previous passes had this centred (no left edge, so nothing read as
           resting on anything) and then left-stepped (a staircase, which drifts
           sideways off the page). A TRIANGLE is the right shape: it is what a
           thing built on a foundation actually looks like, and it is symmetrical
           so it sits on the page's centre axis with everything else.

           Mechanically: each course is a centred bar. The widest is Pre-algebra
           at the base, each one above it narrower, so the stack tapers to
           Calculus at the apex. column-reverse paints the base at the bottom
           while DOM order stays Pre-algebra -> Calculus for screen readers.
           Width is a percentage of the container so it can never overflow. */
        .rb-ladder{list-style:none;margin:var(--s-5) auto 0;padding:0;
          display:flex;flex-direction:column-reverse;align-items:center;
          max-width:min(680px,100%);gap:clamp(4px,.8vh,9px)}
        .rb-ladder__rung{position:relative;box-sizing:border-box;
          display:flex;align-items:center;justify-content:center;gap:var(--s-2);
          /* base is full width; each course above loses ~11% a side */
          width:calc(100% - var(--i) * 11%);
          min-height:clamp(42px,6vh,64px);padding:0 var(--s-3);
          border:1px solid ${T.hairline};border-radius:2px;
          background:linear-gradient(180deg,
            rgba(201,169,98,calc(.015 + var(--i) * .012)),
            rgba(201,169,98,0));
          transition:border-color .4s ease,background .4s ease}
        /* the base course is the one bearing the weight — it reads solid */
        .rb-ladder__rung:first-child{border-color:rgba(201,169,98,.34)}
        .rb-ladder__name{font-family:${T.display};font-weight:400;
          font-size:clamp(.95rem, calc(.9rem + var(--i) * .16rem), 1.9rem);
          line-height:1.05;letter-spacing:-.01em;
          color:${T.parchment};white-space:nowrap;
          opacity:calc(.62 + var(--i) * .076)}
        /* The two unbuilt courses are the APEX, so the pyramid does the work for
           free: they are the top stones not yet set. Drawn as an outline. */
        .rb-ladder__rung--soon{border-style:dashed;background:none}
        .rb-ladder__rung--soon .rb-ladder__name{opacity:.42}
        .rb-ladder__soon{flex-shrink:0;font-family:${T.label};font-size:.5625rem;font-weight:500;
          letter-spacing:.18em;text-indent:.18em;text-transform:uppercase;color:${T.gold};opacity:.7;
          border:.5px solid ${T.hairline};border-radius:2px;padding:4px 9px;
          white-space:nowrap;align-self:center}

        /* PLANS */
        /* Two cards, so cap the grid and centre it — auto-fit with two children
           stretches them across the full 1120px and they read as panels rather
           than a considered pair. 16px radius softened to 4px: heavy rounding is
           a consumer-app signal, hairline-and-square is a couture one. */
        .rb-plans{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));
          gap:var(--s-2);margin:var(--s-5) auto 0;max-width:760px}
        .rb-plan{position:relative;display:flex;flex-direction:column;background:${T.slate};
          border:.5px solid ${T.hairline};border-radius:4px;
          padding:var(--s-5) var(--s-3) var(--s-4);text-align:center}
        .rb-plan--feat{border-color:rgba(201,169,98,0.45);
          box-shadow:0 0 0 1px rgba(201,169,98,0.10),0 20px 60px rgba(0,0,0,0.45)}
        .rb-plan__badge{position:absolute;top:-10px;left:50%;transform:translateX(-50%);
          background:${T.gold};color:${T.ink};font-family:${T.label};font-size:.625rem;font-weight:500;
          letter-spacing:.18em;text-indent:.18em;text-transform:uppercase;
          padding:6px 14px;border-radius:2px;white-space:nowrap}
        .rb-plan__name{font-family:${T.label};font-weight:500;font-size:var(--t-label);
          letter-spacing:.26em;text-indent:.26em;text-transform:uppercase;color:${T.gold};
          margin:0 0 var(--s-2)}
        .rb-plan__line{font-size:var(--t-small);line-height:1.65;color:${T.muted};
          margin:0 auto var(--s-4);min-height:3.3em;max-width:24ch}
        .rb-plan__price{display:flex;align-items:baseline;justify-content:center;gap:4px;
          margin:0 0 var(--s-4);padding-bottom:var(--s-4);border-bottom:.5px solid ${T.hairline}}
        /* Tabular figures so $0 and $20 share a baseline width and the two cards
           line up. Proportional numerals make a price grid look accidental. */
        .rb-plan__amt{font-family:${T.display};font-weight:400;font-size:3.6rem;
          letter-spacing:-.03em;font-variant-numeric:tabular-nums;line-height:1}
        .rb-plan__per{font-size:var(--t-small);color:${T.muted};font-family:${T.label}}
        /* Shown only when Stripe's price isn't available; same height as an amount so the card doesn't jump.
           Caps with the nav links' spacing: buttons are always caps (brand book). */
        .rb-plan__seeprice{background:none;border:0;padding:0;cursor:pointer;font-family:${T.label};
          font-size:var(--t-label);font-weight:500;letter-spacing:.18em;text-indent:.18em;
          text-transform:uppercase;line-height:3.6rem;color:${T.gold};text-decoration:underline;
          text-underline-offset:4px}
        /* Feature rows stay LEFT-aligned inside a centred card. Centring a ragged
           list of different-length lines is the one place centring fails — the
           left edge is what lets the eye scan them. The block itself is centred. */
        .rb-plan__feats{list-style:none;margin:0 auto var(--s-4);padding:0;display:flex;
          flex-direction:column;gap:var(--s-2);flex:1;text-align:left;width:fit-content}
        .rb-plan__feats li{display:flex;gap:12px;font-size:var(--t-small);line-height:1.55;
          color:${T.parchment};opacity:.85;max-width:30ch}
        .rb-plan__tick{flex-shrink:0;margin-top:4px;line-height:0}
        .rb-plan__cta{width:100%}

        /* CLOSE */
        /* ═══ STATEMENT PANELS ════════════════════════════════════════════
           Full-bleed, darker ground, no chrome. Deliberately NOT a .rb-sec: no
           eyebrow, no section numeral, no button. A section is where the page
           explains; a panel is where it stops explaining. Set at ~70% of the hero
           so the hero remains the loudest thing on the page. */
        .rb-stmt{position:relative;width:100%;background:${T.void};
          border-top:.5px solid ${T.hairline};border-bottom:.5px solid ${T.hairline};
          padding:var(--s-7) 24px;text-align:center;
          opacity:0;transform:translateY(18px);
          transition:opacity .9s ease,transform .9s cubic-bezier(.2,.7,.3,1)}
        .rb-stmt.is-in{opacity:1;transform:none}
        .rb-stmt__h{font-family:${T.display};font-weight:400;
          font-size:min(5.6vw,4.25rem);line-height:1.02;letter-spacing:-.018em;
          text-transform:uppercase;margin:0 auto;max-width:min(100%, var(--m-tight));
          text-wrap:balance;color:${T.parchment}}
        /* Wider than --m-read (46ch) on purpose. That measure exists for reading a
           PARAGRAPH; these are three deliberate lines, and the longest is 61
           characters. At 46ch it broke mid-thought and the cadence was lost.
           On a phone it still wraps — unavoidable at 375px — but balance splits it
           evenly rather than orphaning a word. */
        .rb-stmt__p{font-size:var(--t-lead);line-height:1.7;color:${T.muted};
          max-width:min(94%, 64ch);margin:var(--s-4) auto 0;text-wrap:pretty}
        /* Each line its own block, so the three-beat cadence survives. text-wrap
           :balance on the long first line keeps it from breaking one word onto a
           second row on narrow screens. */
        .rb-stmt__line{display:block;text-wrap:balance}
        .rb-stmt__line + .rb-stmt__line{margin-top:.35em}
        /* the supporting line arrives a beat after the statement */
        .rb-stmt__p{transition:opacity .8s ease .18s,transform .8s cubic-bezier(.2,.7,.3,1) .18s}

        .rb-close{text-align:center;padding:var(--s-7) 24px calc(var(--s-7) + var(--s-3));
          background:radial-gradient(ellipse at 50% 120%, rgba(201,169,98,0.14) 0%, transparent 62%);
          opacity:0;transform:translateY(22px);transition:opacity .8s ease,transform .8s cubic-bezier(.2,.7,.3,1)}
        .rb-close.is-in{opacity:1;transform:none}
        .rb-close > *{transition:opacity .8s ease,transform .8s cubic-bezier(.2,.7,.3,1)}
        /* Mirrors the hero: caps, positive tracking, weight 300. The page opens
           and closes on the same gesture. */
        /* Mirrors the hero exactly — same face, same weight, same negative
           tracking. The page opens and closes on one gesture. */
        .rb-close__h{font-family:${T.display};font-weight:400;
          font-size:min(6.4vw,4.75rem);line-height:.98;letter-spacing:-.015em;
          text-transform:uppercase;margin:0 auto var(--s-5);
          max-width:min(100%, var(--m-tight));text-wrap:balance}
        .rb-close__h em{font-style:italic;color:${T.gild};letter-spacing:-.005em}

        .rb-foot{display:flex;flex-wrap:wrap;gap:14px;align-items:center;justify-content:space-between;
          padding:26px 24px calc(26px + env(safe-area-inset-bottom));border-top:.5px solid ${T.hairline};
          font-size:var(--t-small);color:${T.muted}}
        .rb-foot__links{display:flex;gap:20px}
        .rb-foot__links a{color:${T.muted};text-decoration:none;font-family:${T.label};
          font-size:var(--t-label);letter-spacing:.16em;text-transform:uppercase}
        @media (hover:hover){.rb-foot__links a:hover{color:${T.parchment}}}

        /* SHORT VIEWPORTS (a 720p laptop, a phone in landscape). The hero is a
           three-line headline plus an orrery; on <760px of height something has
           to give, and the right thing to shed is the decorative element rather
           than the type or the CTA. */
        /* 820, not 760. A 1366x768 laptop is one of the commonest screens there is
           and it fell 3px the wrong side of a 760 breakpoint — the scroll cue was
           cut by three pixels, which is the worst kind of nearly-working. */
        @media (max-height:820px){
          /* 1280x720 is a real and common laptop. Measured, the scroll cue landed
             22px below the fold — so the orrery loses a little more and the gaps
             tighten. The cue is the only thing telling a visitor the page
             continues; it must never be the thing that gets cut. */
          .rb-orrery{width:min(210px,52vw,22svh)}
          .rb-hero{padding-top:12px;padding-bottom:36px}
          .rb-hero__sub{margin-bottom:20px}
        }
        @media (max-height:600px){
          /* Landscape phone: ~390px of height cannot hold an orrery, a display
             headline and a CTA. Drop the decoration, bring the headline down to a
             single tracked line, and let the hero size to its content rather than
             claiming a full screen. The scroll cue goes too — the page is already
             scrolling by definition at this height. */
          .rb-orrery{display:none}
          .rb-scroll{display:none}
          .rb-hero{min-height:auto;padding:24px 24px 32px}
          .rb-hero__h{font-size:clamp(28px,5.2vw,42px);line-height:1;margin-bottom:14px}
          .rb-hero__sub{font-size:.875rem;line-height:1.55;margin-bottom:var(--s-3);max-width:var(--m-read)}
          .rb-hero__note{margin-top:12px}
          .rb-btn{min-height:46px;padding:0 26px}
        }

        @media (max-width:640px){
          .rb-sec{padding:64px 20px}
          .rb-hero{padding:16px 20px 44px;min-height:calc(100svh - 66px)}
          .rb-plan__line{min-height:0}
          .rb-nav__link{display:none}
        }

        @media (prefers-reduced-motion:reduce){
          .rb-land *,.rb-land *::before,.rb-land *::after{
            animation-duration:.001ms !important;animation-iteration-count:1 !important;
            transition-duration:.001ms !important}
          .rb-sec,.rb-sec > *,.rb-close,.rb-close > *,.rb-hero__copy,.rb-hero__copy > *,
          .rb-orrery{opacity:1 !important;transform:none !important;transition-delay:0s !important}
          .rb-sec::before{transform:scaleX(1) !important}
        }
      `}</style>
    </div>
  );
}
